import React, { forwardRef, useId } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';

const controlStyles = {
	control: (base, state) => ({
		...base,
		minHeight: 38,
		borderRadius: 'var(--q-radius-md, 8px)',
		borderColor: state.isFocused
			? 'var(--q-brand-500, #187A8C)'
			: 'var(--q-surface-border, #e2e8f0)',
		boxShadow: state.isFocused
			? 'var(--q-focus-ring, 0 0 0 3px rgba(24, 122, 140, .35))'
			: 'none',
		backgroundColor: state.isDisabled
			? 'var(--q-gray-50, #f8fafc)'
			: 'var(--q-surface-bg, #ffffff)',
		cursor: state.isDisabled ? 'not-allowed' : 'pointer',
		transition:
			'border-color var(--q-transition-fast, 150ms ease), box-shadow var(--q-transition-fast, 150ms ease)',
		'&:hover': {
			borderColor: state.isDisabled
				? 'var(--q-surface-border, #e2e8f0)'
				: 'var(--q-brand-500, #187A8C)',
		},
	}),
	valueContainer: (base) => ({
		...base,
		minHeight: 36,
		padding: '2px 10px',
	}),
	singleValue: (base) => ({
		...base,
		color: 'var(--q-text-primary, #0f172a)',
		fontFamily: 'var(--q-font-family, inherit)',
		fontSize: '0.875rem',
	}),
	placeholder: (base) => ({
		...base,
		color: 'var(--q-text-muted, #94a3b8)',
		fontFamily: 'var(--q-font-family, inherit)',
		fontSize: '0.875rem',
	}),
	input: (base) => ({
		...base,
		color: 'var(--q-text-primary, #0f172a)',
		fontFamily: 'var(--q-font-family, inherit)',
		fontSize: '0.875rem',
	}),
	menuPortal: (base) => ({ ...base, zIndex: 3000 }),
	menu: (base) => ({
		...base,
		zIndex: 3000,
		border: '1px solid var(--q-surface-border, #e2e8f0)',
		borderRadius: 'var(--q-radius-md, 8px)',
		overflow: 'hidden',
		boxShadow:
			'var(--q-surface-shadow, 0 4px 12px rgba(15, 23, 42, .12))',
		backgroundColor: 'var(--q-surface-bg, #ffffff)',
	}),
	option: (base, state) => ({
		...base,
		backgroundColor: state.isSelected
			? 'var(--q-brand-50, #eff9fa)'
			: state.isFocused
			? 'var(--q-bg-subtle, #f1f5f9)'
			: 'transparent',
		color: state.isSelected
			? 'var(--q-brand-700, #0e525f)'
			: 'var(--q-text-primary, #0f172a)',
		fontWeight: state.isSelected ? 600 : 400,
		fontSize: '0.875rem',
		fontFamily: 'var(--q-font-family, inherit)',
		cursor: state.isDisabled ? 'not-allowed' : 'pointer',
		padding: '8px 12px',
		'&:active': {
			backgroundColor: 'var(--q-brand-100, #d5f0f3)',
		},
	}),
};

const spacing = { xs: 8, sm: 12, md: 16, lg: 24, xl: 32 };
const withSpacing = (value) =>
	typeof value === 'string'
		? spacing[value] ?? value
		: typeof value === 'number'
		? `${value}px`
		: value;

function extractText(node) {
	if (node == null) return '';
	if (typeof node === 'string' || typeof node === 'number') return String(node);
	if (Array.isArray(node)) return node.map(extractText).join(' ');
	if (node.props?.children) return extractText(node.props.children);
	return '';
}

const normalizeOption = (option) => {
	if (option == null) return { value: '', label: '' };
	if (typeof option === 'string' || typeof option === 'number') {
		return { value: String(option), label: String(option) };
	}
	const value = option.value !== undefined ? option.value : option.id;
	const label = option.label !== undefined ? option.label : option.name;
	return {
		...option,
		value: value != null ? String(value) : '',
		label: label != null ? label : String(value ?? ''),
	};
};

export const FormSelect = forwardRef(({
	data,
	options: propOptions,
	value,
	onChange,
	label,
	withAsterisk,
	description,
	hint,
	error,
	placeholder = 'Select an option',
	clearable = false,
	searchable,
	disabled = false,
	nothingFoundMessage = 'No options found',
	maxDropdownHeight,
	allowDeselect,
	mt,
	mb,
	w,
	flex,
	size = 'md',
	style,
	className,
	wrapperClassName,
	id,
	...restProps
}, ref) => {
	const generatedId = useId();
	const inputId = id || generatedId;

	const rawList = data ?? propOptions ?? [];
	const normalizedOptions = rawList.map(normalizeOption);

	// Smart search: Explicit prop takes priority; otherwise auto-enable if > 8 items
	const isSearchable =
		searchable !== undefined ? Boolean(searchable) : normalizedOptions.length > 8;

	// Resolve selected option object from value
	const stringValue = value != null && typeof value === 'object' && 'value' in value
		? String(value.value)
		: value != null
		? String(value)
		: null;

	const selectedOption = stringValue != null
		? normalizedOptions.find((opt) => String(opt.value) === stringValue) || null
		: null;

	const handleChange = (selected) => {
		const nextVal = selected ? selected.value : clearable ? null : '';
		onChange?.(nextVal);
	};

	const filterOption = (candidate, input) => {
		if (!input) return true;
		const searchLower = input.toLowerCase().trim();
		const labelText = extractText(candidate.label).toLowerCase();
		const valText = String(candidate.value || '').toLowerCase();
		return labelText.includes(searchLower) || valText.includes(searchLower);
	};

	const sizeMinHeight = size === 'sm' || size === 'xs' ? 32 : size === 'lg' || size === 'xl' ? 44 : 38;

	const dynamicStyles = {
		...controlStyles,
		control: (base, state) => ({
			...controlStyles.control(base, state),
			minHeight: sizeMinHeight,
			borderColor: error
				? 'var(--q-danger-solid, #dc2626)'
				: state.isFocused
				? 'var(--q-brand-500, #187A8C)'
				: 'var(--q-surface-border, #e2e8f0)',
		}),
		valueContainer: (base) => ({
			...controlStyles.valueContainer(base),
			minHeight: sizeMinHeight - 2,
		}),
		...(maxDropdownHeight
			? {
					menuList: (base) => ({
						...base,
						maxHeight: maxDropdownHeight,
					}),
			  }
			: {}),
	};

	const wrapperStyle = {
		marginTop: withSpacing(mt),
		marginBottom: withSpacing(mb),
		width: withSpacing(w),
		flex,
		...style,
	};

	return (
		<div
			className={`q-form-group ${wrapperClassName || ''}`.trim()}
			style={wrapperStyle}
		>
			{label && (
				<label className="q-form-label" htmlFor={inputId}>
					{label}
					{withAsterisk && <span className="q-form-required"> *</span>}
				</label>
			)}

			<Select
				ref={ref}
				inputId={inputId}
				instanceId={inputId}
				options={normalizedOptions}
				value={selectedOption}
				onChange={handleChange}
				isDisabled={disabled}
				isClearable={clearable}
				isSearchable={isSearchable}
				placeholder={placeholder}
				noOptionsMessage={() => nothingFoundMessage}
				filterOption={filterOption}
				className={className}
				classNamePrefix="q-select-dropdown"
				styles={dynamicStyles}
				menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
				menuPosition="fixed"
				{...restProps}
			/>

			{error ? (
				<div className="q-form-error">{error}</div>
			) : hint || description ? (
				<div className="q-form-hint">{hint ?? description}</div>
			) : null}
		</div>
	);
});

FormSelect.displayName = 'FormSelect';

FormSelect.propTypes = {
	data: PropTypes.array,
	options: PropTypes.array,
	value: PropTypes.any,
	onChange: PropTypes.func,
	label: PropTypes.node,
	withAsterisk: PropTypes.bool,
	description: PropTypes.node,
	hint: PropTypes.node,
	error: PropTypes.node,
	placeholder: PropTypes.node,
	clearable: PropTypes.bool,
	searchable: PropTypes.bool,
	disabled: PropTypes.bool,
	nothingFoundMessage: PropTypes.node,
	maxDropdownHeight: PropTypes.number,
	allowDeselect: PropTypes.bool,
	flex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	size: PropTypes.string,
	mt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	mb: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	w: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	style: PropTypes.object,
	className: PropTypes.string,
	wrapperClassName: PropTypes.string,
	id: PropTypes.string,
};

export default FormSelect;
